/**
 * Centralized auth helpers - single point of token I/O so storage strategy can be
 * swapped without touching call sites.
 *
 * NOTE: We use sessionStorage as the default (cleared on tab close, smaller XSS
 * window than localStorage) with a localStorage fallback for the "remember me"
 * use-case. Tokens are JWTs with a short (24h) server-side expiry.
 */

const KEY = "melopa_admin_token";
const USER_KEY = "melopa_admin_user";

function storage(remember) {
  // Caller can opt-in to persistent storage; default is sessionStorage.
  if (remember) return window.localStorage;
  return window.sessionStorage;
}

export function getToken() {
  // Read from session first, fall back to localStorage for "remember me".
  try {
    return (
      window.sessionStorage.getItem(KEY) ||
      window.localStorage.getItem(KEY) ||
      null
    );
  } catch {
    return null;
  }
}

export function setToken(token, { remember = false } = {}) {
  try {
    storage(remember).setItem(KEY, token);
    // Make sure the other store doesn't hold a stale token.
    storage(!remember).removeItem(KEY);
  } catch {
    /* storage unavailable */
  }
}

export function clearToken() {
  try {
    window.sessionStorage.removeItem(KEY);
    window.sessionStorage.removeItem(USER_KEY);
    window.localStorage.removeItem(KEY);
    window.localStorage.removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function setUser(user, { remember = false } = {}) {
  try {
    storage(remember).setItem(USER_KEY, JSON.stringify(user));
    storage(!remember).removeItem(USER_KEY);
  } catch {
    /* ignore */
  }
}

export function getUser() {
  try {
    const raw =
      window.sessionStorage.getItem(USER_KEY) ||
      window.localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isAuthed() {
  return Boolean(getToken());
}
